import { useAuth0 } from '@auth0/auth0-react'
import useAuth from 'hooks/useAuth'
import useLocalStorage from 'hooks/useLocalStorage'
import { useOrganizationStatus } from 'hooks/useOrganizationStatus'
import React, { createContext, useContext, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import type { Organization } from 'types/organization'
import type { PurcentAlertType, PurcentAppState } from 'types/purcent-app-state'
import { checkPermissions } from 'types/user-profile'
import type { OrganizationStatusResponse } from 'types/organization'
import { activeSubscriptionCheck, checkSubscriptionFeature } from 'types/subscriptions'

const initialState: PurcentAppState = {
  selectedOrganization: null,
  alert: null,
}

const GlobalStateContext = createContext<GlobalStateContextValue>({
  purcentAppState: null,
  onChangeOrganizationSelection: () => {},
  setAlert: () => {},
  refetchStatus: () => {},
  checkSubscriptionTierFeature: () => {
    console.log('LOSER!')
    return false
  },
  isLoadingSubscriptionStatus: true,
  isSubscriptionStatusLoaded: false,
})

interface GlobalStateWrapperProps {
  children: React.ReactNode
}

interface GlobalStateContextValue {
  purcentAppState: PurcentAppState | null
  onChangeOrganizationSelection: (org: Organization) => void
  setAlert: (alert: PurcentAlertType | null) => void
  refetchStatus: () => void
  checkSubscriptionTierFeature: (featureName: string) => boolean
  isLoadingSubscriptionStatus: boolean
  isSubscriptionStatusLoaded: boolean
}

const GlobalStateProvider: React.FC<GlobalStateWrapperProps> = ({ children }) => {
  const {
    auth0User: user,
    token,
    isLoggedIn,
    isLoading: isLoadingAuth,
    profile,
    isLoading,
    errorProfile: isError,
    requestStatus: status,
  } = useAuth()

  const [purcentAppState, setPurcentAppState] = useLocalStorage<PurcentAppState>(
    'purcent-app-state',
    initialState,
    profile?.user?.id
  )
  const { loginWithRedirect } = useAuth0()

  const {
    organizationStatus,
    isLoadingOrganizationStatus,
    isError: isErrorOrganizationStatus,
    handleRefetch,
  } = useOrganizationStatus(token || '', null)

  const navigate = useNavigate()

  const refetchStatus = useCallback(() => {
    handleRefetch()
  }, [handleRefetch])

  const checkForActiveSubscription = useCallback((orgStatus: OrganizationStatusResponse): boolean => {
    if (!orgStatus.subscriptionInformation) {
      return false
    }
    return activeSubscriptionCheck(orgStatus.subscriptionInformation)
  }, [])

  // Helper function to handle subscription and access navigation
  const handleSubscriptionAndAccessNavigation = useCallback(
    (orgStatus: OrganizationStatusResponse, userProfile: typeof profile) => {
      if (!userProfile) return

      const userRoles = userProfile.roles?.map((role) => role.role.name) ?? []
      const allowedRoles = orgStatus.allowed?.roles ?? []
      const isAllowed = userRoles.some((userRole) => allowedRoles.includes(userRole))
      const activeSubscription = checkForActiveSubscription(orgStatus)

      // Handle unauthorized users
      if (!isAllowed) {
        if (!activeSubscription) {
          navigate('/access-error')
          return
        }
        if (orgStatus.status === 'SETUP') {
          navigate('/setup-in-progress')
        }
        return
      }

      // Handle inactive subscription for authorized users
      if (!activeSubscription) {
        const isAdmin = checkPermissions(userProfile, ['settings.write', 'organization.write'])
        if (isAdmin) {
          navigate('/manage-subscription')
        } else {
          navigate('/access-error')
        }
      }
    },
    [checkForActiveSubscription, navigate]
  )

  const onChangeOrganizationSelection = useCallback(
    (org: Organization) => {
      setPurcentAppState((prevState) => ({
        ...prevState,
        selectedOrganization: org,
      }))
    },
    [setPurcentAppState]
  )

  const setAlert = useCallback(
    (alert: PurcentAlertType | null) => {
      setPurcentAppState((prevState) => ({
        ...prevState,
        alert,
      }))
    },
    [setPurcentAppState]
  )

  const checkSubscriptionTierFeature = useCallback(
    (featureName: string): boolean => {
      if (!organizationStatus?.subscriptionInformation) {
        return false
      }
      return checkSubscriptionFeature(organizationStatus.subscriptionInformation, featureName)
    },
    [organizationStatus]
  )

  // Update alerts based on organization status
  useEffect(() => {
    if (isLoadingOrganizationStatus || isErrorOrganizationStatus) return

    if (organizationStatus?.alerts && organizationStatus.alerts.length > 0) {
      setAlert({
        level: organizationStatus.alerts[0].level as PurcentAlertType['level'],
        message: organizationStatus.alerts[0].message,
        actionMessage: organizationStatus.alerts[0].actionMessage,
        link: organizationStatus.alerts[0].actionLink,
      })
    } else {
      setAlert(null)
    }
    // setAlert is stable due to useCallback with updater function, no need in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationStatus, isLoadingOrganizationStatus, isErrorOrganizationStatus])

  // Handle subscription and access control navigation
  useEffect(() => {
    if (isLoadingOrganizationStatus || isErrorOrganizationStatus || isLoading || !profile) return

    if (organizationStatus) {
      handleSubscriptionAndAccessNavigation(organizationStatus, profile)
    }
  }, [
    organizationStatus,
    isLoadingOrganizationStatus,
    isErrorOrganizationStatus,
    profile,
    isLoading,
    handleSubscriptionAndAccessNavigation,
  ])

  // Handle global redirection for authentication errors
  useEffect(() => {
    if (isLoadingAuth || isLoggedIn) return

    if (isError || status === 401) {
      localStorage.clear()
      loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      })
    }
  }, [isError, status, isLoadingAuth, isLoggedIn, loginWithRedirect])

  // Handle inactive users
  useEffect(() => {
    if (isLoading || !profile) return

    if (profile.error === 'User is not active') {
      navigate('/not-active', { replace: true })
    }
  }, [profile, isLoading, navigate])

  // Handle automatic organization selection
  useEffect(() => {
    if (isLoading || isError || !user || !profile) return

    // Skip organization selection if user is not active
    if (profile.error === 'User is not active') return

    const firstOrg = profile.roles?.[0]?.organization ?? null
    const currentOrgId = purcentAppState.selectedOrganization?.id

    // Set first organization if none selected or if current selection is invalid
    if (firstOrg && (!currentOrgId || currentOrgId !== firstOrg.id)) {
      setPurcentAppState((prevState) => ({
        ...prevState,
        selectedOrganization: firstOrg,
      }))
    } else if (!firstOrg && purcentAppState.selectedOrganization) {
      // Clear selection if no organizations available
      setPurcentAppState(() => initialState)
    }
    // setPurcentAppState is stable from useLocalStorage, no need in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile, isLoading, isError, purcentAppState.selectedOrganization?.id])

  return (
    <GlobalStateContext.Provider
      value={{
        purcentAppState,
        onChangeOrganizationSelection,
        setAlert,
        refetchStatus,
        checkSubscriptionTierFeature,
        isLoadingSubscriptionStatus: isLoadingOrganizationStatus,
        isSubscriptionStatusLoaded: !isLoadingOrganizationStatus && organizationStatus != null,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  )
}

function useGlobalState() {
  const context = useContext(GlobalStateContext)
  if (!context) {
    throw new Error('useGlobalState must be used within a GlobalStateContext')
  }
  return context
}

export { GlobalStateProvider, useGlobalState }
